// Ordered list of styleframes mixing already uploaded images and new files.

import React, { useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TrashIcon, PhotoIcon } from "@heroicons/react/20/solid";

export interface StyleframeItem {
  key: string;
  /** Already uploaded image. */
  url?: string;
  /** New image waiting to be uploaded on save. */
  blob?: Blob;
  name?: string;
  preview: string;
}

let counter = 0;
export const newItemKey = () => `sf-${Date.now()}-${counter++}`;

function SortableFrame({
  item,
  index,
  onRemove,
  onUseAsThumbnail,
}: {
  item: StyleframeItem;
  index: number;
  onRemove: () => void;
  onUseAsThumbnail?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group relative rounded-md overflow-hidden border border-neutral-200 bg-neutral-100 ${
        isDragging ? "z-10 shadow-lg ring-2 ring-neutral-900" : ""
      }`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing" aria-label={`Styleframe ${index + 1}, glisser pour réordonner`}>
        <img src={item.preview} alt="" loading="lazy" className="w-full aspect-video object-cover pointer-events-none select-none" />
      </div>
      <span className="absolute top-1.5 left-1.5 text-[11px] font-mono bg-black/70 text-white px-1.5 py-0.5 rounded">
        {String(index + 1).padStart(2, "0")}
      </span>
      {item.blob && (
        <span className="absolute bottom-1.5 left-1.5 text-[11px] bg-amber-400 text-amber-950 px-1.5 py-0.5 rounded">
          nouvelle
        </span>
      )}
      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        {onUseAsThumbnail && (
          <button
            type="button"
            onClick={onUseAsThumbnail}
            className="bg-white/90 hover:bg-white text-neutral-900 rounded p-1"
            title="Utiliser comme miniature"
          >
            <PhotoIcon className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="bg-white/90 hover:bg-white text-red-700 rounded p-1"
          title="Retirer"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function StyleframesEditor({
  items,
  onChange,
  onExtract,
  onUseAsThumbnail,
}: {
  items: StyleframeItem[];
  onChange: (items: StyleframeItem[]) => void;
  onExtract: () => void;
  onUseAsThumbnail?: (item: StyleframeItem) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addFiles = (files: FileList | File[]) => {
    const images = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    onChange([
      ...items,
      ...images.map((f) => ({ key: newItemKey(), blob: f, name: f.name, preview: URL.createObjectURL(f) })),
    ]);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.key === active.id);
    const to = items.findIndex((i) => i.key === over.id);
    onChange(arrayMove(items, from, to));
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`flex flex-wrap items-center gap-3 rounded-md border border-dashed px-4 py-3 text-sm ${
          dragOver ? "border-neutral-900 bg-neutral-100" : "border-neutral-300"
        }`}
      >
        <span className="text-neutral-600">Déposez des images ici, ou</span>
        <button type="button" className="underline font-medium" onClick={() => inputRef.current?.click()}>
          choisissez des fichiers
        </button>
        <span className="text-neutral-400">·</span>
        <button type="button" className="underline font-medium" onClick={onExtract}>
          extrayez-les d'un fichier vidéo
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {items.length > 0 && (
          <span className="ml-auto text-neutral-500">
            {items.length} image{items.length > 1 ? "s" : ""} · glisser pour réordonner
          </span>
        )}
      </div>

      {items.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.key)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {items.map((item, index) => (
                <SortableFrame
                  key={item.key}
                  item={item}
                  index={index}
                  onRemove={() => onChange(items.filter((i) => i.key !== item.key))}
                  onUseAsThumbnail={onUseAsThumbnail ? () => onUseAsThumbnail(item) : undefined}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
